import { useCallback } from 'react';
import type { Map } from 'leaflet';

interface MarkerPosition {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

/**
 * Hook that provides utility functions for map markers
 * @param leafletMapRef Reference to the Leaflet map
 * @param LRef Reference to the Leaflet library
 * @param activeSpotId Currently active spot ID
 * @param currentSpotId Currently selected spot ID
 * @returns Utility functions for marker management
 */
export const useMapMarkers = (
  leafletMapRef: React.MutableRefObject<Map | null>,
  LRef: React.MutableRefObject<typeof import('leaflet') | null>,
  activeSpotId: string | null,
  currentSpotId: string | null,
) => {
  /**
   * Calculate CSS position style for a marker
   */
  const getMarkerStyle = useCallback(
    (lat: number, lon: number, spotId?: string) => {
      const style: React.CSSProperties = {};

      if (leafletMapRef.current && LRef.current) {
        try {
          const point = leafletMapRef.current.latLngToContainerPoint(LRef.current.latLng(lat, lon));

          if (point) {
            style.left = point.x || 0;
            style.top = point.y || 0;

            if (spotId && activeSpotId === spotId) {
              style.zIndex = 99;
            }
            if (spotId && currentSpotId === spotId) {
              style.zIndex = 98;
            }
          }
        } catch (_e) {
          style.display = 'none';
          return style;
        }
      }

      if (
        (typeof style.left === 'number' && style.left <= 0) ||
        style.left === undefined ||
        (typeof style.top === 'number' && style.top <= 0) ||
        style.top === undefined
      ) {
        style.display = 'none';
      }

      return style;
    },
    [leafletMapRef, LRef, activeSpotId, currentSpotId],
  );

  /**
   * Calculate tooltip position based on marker location within visible bounds
   */
  const getTooltipPosition = useCallback(
    (
      bounds: { N: number; S: number; E: number; W: number },
      spot: { lat: number; lon: number },
    ): MarkerPosition => {
      const vertical = spot.lat <= bounds.S + (3 / 5) * (bounds.N - bounds.S) ? 'top' : 'bottom';
      let horizontal: 'left' | 'center' | 'right';

      if (spot.lon < (bounds.E - bounds.W) / 5 + bounds.W) {
        horizontal = 'right';
      } else if (spot.lon < (bounds.E - bounds.W) * (4 / 5) + bounds.W) {
        horizontal = 'center';
      } else {
        horizontal = 'left';
      }

      return { vertical, horizontal };
    },
    [],
  );

  return {
    getMarkerStyle,
    getTooltipPosition,
  };
};

export default useMapMarkers;
